/* eslint-disable */
// @ts-nocheck

import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'

import { useMutation, useQueryClient } from '@tanstack/react-query'

import { createContainer } from '@/api/containersAPI'
import { ModalWizardSteps, ModalWizzarStepsProvider } from '@/components/new'
import { ModalWizardTabs } from '@/components/new/modalWizardTabs/ModalWizardTabs'
import { ModalWizardTabsProvider } from '@/components/new/modalWizardTabs/ModalWizardTabsContext'
import DetailsExpTab from '@/containers/logistic/expedients/wizzardExpedient/tabs/DetailsExpTab'
import useContainerWizzardSteps from '@/state/useContainerWizzardSteps'

import { IContainer } from '../interfaces/interfaces'
import { useContainerWizard } from './hooks/useContainerWizzard'
import AseguramientoContainerTab from './tabs/aseguramiento/AseguramientoContainerTab'
import CostsContainerTab from './tabs/costs/CostsContainerTab'
import DetailsContainerTab from './tabs/details/DetailsContainerTab'
import DetailsGeneralContainerTab from './tabs/details/DetailsGeneralContainerTab'
import DetailsAdvancedContainerTab from './tabs/detailsAdvanced/DetailsAdvancedContainerTab'
import DocumentsContTab from './tabs/documents/DocumentsContTab'
import ProductsContainerDisplayTab from './tabs/products/ProductsContainerDisplayTab'
import ProductsContainerTab from './tabs/products/ProductsContainerTab'

interface IProps {
  editContainer?: IContainer
  onClose: Function
  showState: boolean
}

export default function ExpedientWizard({
  editContainer,
  onClose,
  showState,
}: IProps) {
  const { currentStep, setCurrentStep, nextStep, prevStep } =
    useContainerWizard()
  const updateData = useContainerWizzardSteps((state) => state.updateData)
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState(0)

  // Exponer función de cierre del modal para que las tabs puedan cerrar el modal
  useEffect(() => {
    if (editContainer) {
      // @ts-ignore - Adding custom property to window for tabs access
      window.closeContainerModal = () => {
        onClose()
      }
    }

    return () => {
      // @ts-ignore
      delete window.closeContainerModal
    }
  }, [editContainer, onClose])

  const createContainerMutation = useMutation({
    mutationFn: async ({
      containerData,
      costs,
      documents,
    }: {
      containerData: any
      costs: any[]
      documents?: any[]
    }) => {
      try {
        // Primero crear el contenedor
        const containerResponse = await createContainer(containerData)
        
        // Debug: Log the complete response structure
        console.log('Complete container response:', containerResponse)
        console.log('Response type:', typeof containerResponse)
        console.log('Response keys:', Object.keys(containerResponse || {}))
        
        if (containerResponse?.data) {
          console.log('Response.data:', containerResponse.data)
          console.log('Response.data type:', typeof containerResponse.data)
          console.log('Response.data keys:', Object.keys(containerResponse.data || {}))
        }

        // También revisar si la respuesta es directamente el contenedor
        if (containerResponse && typeof containerResponse === 'object' && !containerResponse.data) {
          console.log('Direct response (no .data wrapper):', containerResponse)
          console.log('Direct response keys:', Object.keys(containerResponse))
        }

        // Verificar la estructura de la respuesta de forma más robusta
        let containerId
        
        // Estrategia 1: Buscar en containerResponse.data.id
        if (containerResponse?.data?.id) {
          console.log('Found ID in response.data.id:', containerResponse.data.id)
          containerId = containerResponse.data.id
        } 
        // Estrategia 2: Buscar en containerResponse.id (respuesta directa)
        else if (containerResponse?.id) {
          console.log('Found ID in response.id:', containerResponse.id)
          containerId = containerResponse.id
        }
        // Estrategia 3: Buscar en containerResponse.data.container.id
        else if (containerResponse?.data?.container?.id) {
          console.log('Found ID in response.data.container.id:', containerResponse.data.container.id)
          containerId = containerResponse.data.container.id
        }
        // Estrategia 4: Buscar en cualquier propiedad que contenga "id"
        else if (containerResponse?.data && typeof containerResponse.data === 'object') {
          console.log('Searching for ID in response.data object...')
          // Buscar el ID en cualquier parte de la estructura de datos
          const searchForId = (obj: any, path: string = ''): any => {
            for (const [key, value] of Object.entries(obj)) {
              const currentPath = path ? `${path}.${key}` : key
              if (key === 'id' && typeof value === 'number') {
                console.log(`Found ID at ${currentPath}:`, value)
                return value
              }
              if (typeof value === 'object' && value !== null) {
                const found = searchForId(value, currentPath)
                if (found) return found
              }
            }
            return null
          }
          
          containerId = searchForId(containerResponse.data)
        }
        // Estrategia 5: Buscar en la respuesta completa
        else if (containerResponse && typeof containerResponse === 'object') {
          console.log('Searching for ID in complete response...')
          const searchForId = (obj: any, path: string = ''): any => {
            for (const [key, value] of Object.entries(obj)) {
              const currentPath = path ? `${path}.${key}` : key
              if (key === 'id' && typeof value === 'number') {
                console.log(`Found ID at ${currentPath}:`, value)
                return value
              }
              if (typeof value === 'object' && value !== null) {
                const found = searchForId(value, currentPath)
                if (found) return found
              }
            }
            return null
          }
          
          containerId = searchForId(containerResponse)
        }

        console.log('Final extracted container ID:', containerId)

        if (!containerId) {
          console.error('Container response structure:', JSON.stringify(containerResponse, null, 2))
          throw new Error('No se pudo obtener el ID del contenedor creado')
        }

        // Luego crear los costos si existen
        if (costs && costs.length > 0) {
          const { postContainerCosts } = await import('@/api/containersAPI')
          for (const cost of costs) {
            const costPayload = {
              fixedCostCategoryId:
                cost.fixedCostCategoryId || cost.categoryId || 1,
              type: cost.type || 'SUPPLIER',
              registeredPrice: {
                amount: Number(
                  cost.cost?.amount ||
                    cost.price?.amount ||
                    cost.registeredPrice?.amount ||
                    0
                ),
                codeCurrency:
                  cost.cost?.codeCurrency ||
                  cost.price?.codeCurrency ||
                  cost.registeredPrice?.codeCurrency ||
                  'USD',
              },
              observations: cost.observations || '',
            }
            await postContainerCosts({
              id: containerId,
              params: costPayload,
            })
          }
        }

        // Luego crear los documentos si existen
        if (documents && documents.length > 0) {
          const { postContainerDocuments } = await import('@/api/containersAPI')
          for (const document of documents) {
            const documentPayload = {
              name: document.name || 'Documento',
              path: document.path || '',
              observations: document.observations || '',
            }
            await postContainerDocuments({
              id: containerId,
              params: documentPayload,
            })
          }
        }

        return containerResponse
      } catch (error) {
        throw error
      }
    },
    onSuccess: () => {
      toast.success('Contenedor creado exitosamente')
      queryClient.invalidateQueries({ queryKey: ['containers'] })
      // Resetear el estado del formulario
      setCurrentStep(0)
      updateData({
        name: '',
        originId: 0,
        countryFromId: 0,
        destinyId: 0,
        categories: [],
        levanteNumber: '',
        trackingNumber: '',
        observations: '',
        documents: [],
        listCosts: [],
        ownerCompany: null,
        importerCompany: null,
        transporterCompany: null,
        storageCompany: null,
        logisticCompany: null,
        isRefrigered: false,
        isPalleted: false,
        code: null,
        sealNumber: null,
        temperatureRange: null,
        transportedBy: null,
        netWeight: null,
        numberOfPallets: null,
      })
      onClose()
    },
    onError: (error: any) => {
      console.error('Error creating container:', error)
      // Solo mostrar error si realmente hay un error
      const errorMessage = error?.response?.data?.message || error?.message
      if (errorMessage) {
        toast.error(errorMessage)
      } else {
        toast.error('Error al crear el contenedor')
      }
    },
  })

  if (!editContainer)
    return (
      <ModalWizzarStepsProvider>
        <ModalWizardSteps
          confirmOnClose={false}
          currentStep={currentStep}
          isOpen={showState}
          isSending={createContainerMutation.isPending}
          onClose={() => {
            setCurrentStep(0)
            onClose()
            updateData({
              name: '',
              originId: 0,
              countryFromId: 0,
              destinyId: 0,
              categories: [],
              levanteNumber: '',
              trackingNumber: '',
              observations: '',
              documents: [],
              listCosts: [],
              ownerCompany: null,
              importerCompany: null,
              transporterCompany: null,
              storageCompany: null,
              logisticCompany: null,
              isRefrigered: false,
              isPalleted: false,
              code: null,
              sealNumber: null,
              temperatureRange: null,
              transportedBy: null,
              netWeight: null,
              numberOfPallets: null,
            })
          }}
          steps={[
            {
              name: 'Detalles generales',
              actionButtons: [
                {
                  label: 'Siguiente',
                  primaryAction: (data) => {
                    nextStep()
                  },
                },
              ],
              component: <DetailsGeneralContainerTab />,
            },
            {
              name: 'Documentación',
              actionButtons: [
                {
                  label: 'Anterior',
                  secondaryAction: () => {
                    prevStep()
                  },
                },
                {
                  label: 'Siguiente',
                  primaryAction: (data) => {
                    nextStep()
                  },
                },
              ],
              component: <DocumentsContTab />,
            },
            {
              name: 'Costos',
              actionButtons: [
                {
                  label: 'Anterior',
                  secondaryAction: () => {
                    prevStep()
                  },
                },
                {
                  label: 'Guardar',
                  primaryAction: (data) => {
                    // Obtener todos los datos del formulario
                    const formData = useContainerWizzardSteps.getState().data

                    // Validar campos requeridos
                    if (!formData.name || formData.name.trim() === '') {
                      toast.error('El nombre del contenedor es requerido')
                      return
                    }

                    // Helper function to convert to number array
                    const convertToNumberArray = (arr: any[]): number[] => {
                      if (!Array.isArray(arr)) return []
                      return arr
                        .map((item: any) => {
                          if (typeof item === 'number') return item
                          if (typeof item === 'string') {
                            const parsed = parseInt(item, 10)
                            return isNaN(parsed) ? null : parsed
                          }
                          if (typeof item === 'object' && item?.id) {
                            const parsed =
                              typeof item.id === 'number'
                                ? item.id
                                : parseInt(item.id, 10)
                            return isNaN(parsed) ? null : parsed
                          }
                          return null
                        })
                        .filter((num): num is number => num !== null && num > 0)
                    }

                    // Helper function to convert to number
                    const convertToNumber = (value: any): number | null => {
                      if (value === null || value === undefined || value === '')
                        return null
                      if (typeof value === 'number') return value
                      if (typeof value === 'string') {
                        const parsed = parseInt(value, 10)
                        return isNaN(parsed) ? null : parsed
                      }
                      if (typeof value === 'object' && value?.id) {
                        const parsed =
                          typeof value.id === 'number'
                            ? value.id
                            : parseInt(value.id, 10)
                        return isNaN(parsed) ? null : parsed
                      }
                      return null
                    }

                    // Preparar los datos para crear el contenedor (SIN costos)
                    const containerData = {
                      name: formData.name.trim(),
                      originId: convertToNumber(formData.originId),
                      countryFromId: convertToNumber(formData.countryFromId),
                      destinyId: convertToNumber(formData.destinyId),
                      categories: convertToNumberArray(
                        formData.categories || []
                      ),
                      levanteNumber: formData.levanteNumber || '',
                      trackingNumber: formData.trackingNumber || '',
                      observations: formData.observations || '',
                      documents: formData.documents || [],
                      ownerCompanyId: convertToNumber(formData.ownerCompany),
                      importerCompanyId: convertToNumber(
                        formData.importerCompany
                      ),
                      transporterCompanyId: convertToNumber(
                        formData.transporterCompany
                      ),
                      storageCompanyId: convertToNumber(
                        formData.storageCompany
                      ),
                      managerCompanyId: convertToNumber(
                        formData.logisticCompany
                      ),
                      ...(formData.isRefrigered === true && {
                        isRefrigered: true,
                      }),
                      ...(formData.isPalleted === true && { isPalleted: true }),
                      code: formData.code ?? null,
                      sealNumber: formData.sealNumber ?? null,
                      temperatureRange: formData.temperatureRange ?? null,
                      transportedBy: formData.transportedBy ?? null,
                      netWeight: convertToNumber(formData.netWeight),
                      numberOfPallets: convertToNumber(
                        formData.numberOfPallets
                      ),
                    }

                    // Crear el contenedor (los costos se crearán en el onSuccess)
                    createContainerMutation.mutate({
                      containerData,
                      costs: formData.listCosts || [],
                      documents: formData.documents || [],
                    })
                  },
                },
              ],
              component: <CostsContainerTab />,
            },
          ]}
          title={'Nuevo contenedor'}
        />
      </ModalWizzarStepsProvider>
    )
  else
    return (
      <ModalWizardTabsProvider>
        <ModalWizardTabs
          isOpen={!!editContainer}
          isSending={false}
          onClose={() => {
            onClose()
            updateData({
              name: '',
              originId: 0,
              destinyId: 0,
              countryFromId: 0,
              categories: [],
              levanteNumber: '',
              trackingNumber: '',
              observations: '',
              documents: [],
              listCosts: [],
              ownerCompany: null,
              importerCompany: null,
              transporterCompany: null,
              storageCompany: null,
              logisticCompany: null,
              isRefrigered: false,
              isPalleted: false,
              code: null,
              sealNumber: null,
              temperatureRange: null,
              transportedBy: null,
              netWeight: null,
              numberOfPallets: null,
            })
          }}
          onTabChange={(idx) => setActiveTab(idx)}
          showDialogAlert={false}
          steps={[
            {
              name: 'Detalles',
              actionButtons: [
                {
                  label: (() => {
                    // @ts-ignore
                    return window.isSavingDetails ? 'Guardando...' : 'Guardar'
                  })(),
                  primaryAction: (data: any) => {
                    // @ts-ignore
                    if (window.handleSaveDetails && !window.isSavingDetails) {
                      // @ts-ignore
                      window.handleSaveDetails()
                    }
                  },
                },
              ],
              component: (
                <DetailsContainerTab editContainer={editContainer} isEditing />
              ),
            },
            {
              name: 'Detalles avanzados',
              actionButtons: [
                {
                  label: (() => {
                    // @ts-ignore
                    return window.isSavingAdvancedDetails
                      ? 'Guardando...'
                      : 'Guardar'
                  })(),
                  primaryAction: (data: any) => {
                    // @ts-ignore
                    if (
                      window.handleSaveAdvancedDetails &&
                      !window.isSavingAdvancedDetails
                    ) {
                      // @ts-ignore
                      window.handleSaveAdvancedDetails()
                    }
                  },
                },
              ],
              component: (
                <DetailsAdvancedContainerTab
                  editContainer={editContainer}
                  isEditing
                />
              ),
            },
            {
              name: 'Aseguramiento',
              actionButtons: [
                {
                  label: (() => {
                    // @ts-ignore
                    return window.isSavingAseguramiento
                      ? 'Guardando...'
                      : 'Guardar'
                  })(),
                  primaryAction: (data: any) => {
                    // @ts-ignore
                    if (
                      window.handleSaveAseguramiento &&
                      !window.isSavingAseguramiento
                    ) {
                      // @ts-ignore
                      window.handleSaveAseguramiento()
                    }
                  },
                },
              ],
              component: (
                <AseguramientoContainerTab
                  editContainer={editContainer}
                  isEditing
                />
              ),
            },
            {
              name: 'Productos/Lote',
              actionButtons: [
                {
                  label: (() => {
                    // @ts-ignore
                    return window.isSavingProducts ? 'Guardando...' : 'Guardar'
                  })(),
                  primaryAction: (data: any) => {
                    // @ts-ignore
                    if (
                      window.handleSaveProducts &&
                      !window.isSavingProducts
                    ) {
                      // @ts-ignore
                      window.handleSaveProducts()
                    }
                  },
                },
              ],
              component: (
                <ProductsContainerTab editContainer={editContainer} isEditing />
              ),
            },
            {
              name: 'Documentos',
              actionButtons: [
                {
                  label: (() => {
                    // @ts-ignore
                    return window.isSavingDocuments ? 'Guardando...' : 'Guardar'
                  })(),
                  primaryAction: (data: any) => {
                    // @ts-ignore
                    if (
                      window.handleSaveDocuments &&
                      !window.isSavingDocuments
                    ) {
                      // @ts-ignore
                      window.handleSaveDocuments()
                    }
                  },
                },
              ],
              component: (
                <DocumentsContTab editContainer={editContainer} isEditing />
              ),
            },
            {
              name: 'Costos',
              actionButtons: [
                {
                  label: (() => {
                    // @ts-ignore
                    return window.isSavingCosts ? 'Guardando...' : 'Guardar'
                  })(),
                  primaryAction: (data: any) => {
                    // @ts-ignore
                    if (window.handleSaveCosts && !window.isSavingCosts) {
                      // @ts-ignore
                      window.handleSaveCosts()
                    }
                  },
                },
              ],
              component: (
                <CostsContainerTab editContainer={editContainer} isEditing />
              ),
            },
          ]}
          title={'Editar contenedor'}
        />
      </ModalWizardTabsProvider>
    )
}
